import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Pencil, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { imageForSchool } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { EmojiRating } from "@/components/EmojiRating";
import { ReviewCard } from "@/components/ReviewCard";
import { PremiumSidebarAd } from "@/components/DemoMonetization";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS } from "@/config/adSlots";
import { ShareMenu } from "@/components/ShareMenu";
import { ReviewDisclaimer } from "@/components/ReviewDisclaimer";
import { SchoolSeoParagraph } from "@/components/SchoolSeoParagraph";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { schoolSchema } from "@/lib/schema";

export default function SchoolDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [reviewsData, setReviewsData] = useState({ items: [], breakdown: {}, total: 0 });
  const [showForm, setShowForm] = useState(false);
  const [comment, setComment] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false); // modal post-invio con CTA share
  // Nuovo form guidato: 5 dimensioni emoji (opzionali, max 2 vuote) + would_choose_again (opzionale)
  const [dims, setDims] = useState({
    teachers_rating: null,
    spaces_rating: null,
    communication_rating: null,
    food_rating: null,
    activities_rating: null,
  });
  const [wouldChoose, setWouldChoose] = useState(null);

  // Ruolo del recensore, derivato dal profilo utente (già impostato in registrazione).
  // "professionista" senza "genitore" => professionista. Altrimenti => genitore (default).
  const userRoles = user?.roles || [];
  const isProfessional = userRoles.includes("professionista") && !userRoles.includes("genitore");
  const reviewerRole = isProfessional ? "professionista" : "genitore";

  const loadAll = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get(`/schools/${id}`),
        api.get(`/schools/${id}/reviews`),
      ]);
      setSchool(s.data);
      setReviewsData(r.data);
    } catch {
      toast.error("Impossibile caricare la scheda");
    }
  };

  const reloadReviews = async () => {
    try {
      const r = await api.get(`/schools/${id}/reviews`);
      setReviewsData(r.data);
    } catch { /* ignore */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [id]);

  // Debug/preview: ?preview=thanks apre il modal post-recensione senza dover inviare davvero.
  // Serve a mostrare rapidamente la schermata di download guida (es. per QA o review stakeholder).
  useEffect(() => {
    if (!school) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("preview") === "thanks") setThanksOpen(true);
  }, [school]);

  // Conversion event: user has stayed >30s on this school page (real interest signal).
  // Fires once per school visit. Used by Google Ads Smart Bidding.
  useEffect(() => {
    if (!school) return;
    const t = setTimeout(() => {
      trackEvent("school_view_engaged", { school_id: school.id, city: school.city });
    }, 30000);
    return () => clearTimeout(t);
  }, [school]);

  // SEO meta + JSON-LD (invisibili all'utente)
  useEffect(() => {
    if (!school) return;
    const stars = school.avg_rating ? `⭐ ${Number(school.avg_rating).toFixed(1)}/5` : "";
    const rev = school.review_count ? ` · ${school.review_count} esperienze` : "";
    const typeLabel = school.type === "pubblica" ? "Pubblica statale" : "Paritaria/Privata";
    const title = `${school.name} — ${school.city} · Per manina`;
    const description = `Scuola dell'infanzia ${typeLabel} a ${school.city}. ${school.address}. ${stars}${rev}. Leggi le esperienze dei genitori e valuta la struttura su Per manina.`.slice(0, 250);
    setPageMeta({
      title,
      description,
      canonicalPath: `/scuola/${school.id}`,
      ogType: "profile",
      ogImage: imageForSchool(school.id),
    });
    injectJsonLd("schema-school", schoolSchema(school, reviewsData.items || []));
    return () => removeJsonLd("schema-school");
  }, [school, reviewsData]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    sessionStorage.setItem("return_to", window.location.pathname);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openReviewForm = () => {
    setShowForm(true);
    // Wait for the form to render, then smoothly scroll it into view.
    setTimeout(() => {
      const form = document.querySelector('[data-testid="review-form"]');
      if (form) {
        form.scrollIntoView({ behavior: "smooth", block: "start" });
        // Small offset to leave breathing space under the sticky header
        setTimeout(() => window.scrollBy({ top: -80, behavior: "smooth" }), 350);
      }
    }, 30);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    const emojiValues = Object.values(dims);
    const blanks = emojiValues.filter((v) => v === null).length;
    if (blanks > 2) {
      toast.error("Puoi lasciare vuote al massimo 2 valutazioni su 5");
      return;
    }
    if (comment.trim().length < 20) {
      toast.error("Scrivi almeno una breve frase (min. 20 caratteri) su cosa vorresti far sapere agli altri genitori");
      return;
    }
    if (!privacy) { toast.error("Devi accettare la Privacy Policy"); return; }
    setSubmitting(true);
    try {
      // rating complessivo ora derivato lato backend dalla media delle emoji
      await api.post(`/schools/${id}/reviews`, {
        comment: comment.trim(),
        reviewer_role: reviewerRole,
        ...dims,
        would_choose_again: wouldChoose,
      });
      toast.success("Grazie per aver raccontato la tua esperienza!");
      trackEvent("review_submitted", { review_type: "school", school_id: id });
      setComment(""); setPrivacy(false); setShowForm(false);
      setDims({
        teachers_rating: null, spaces_rating: null,
        communication_rating: null, food_rating: null, activities_rating: null,
      });
      setWouldChoose(null);
      setThanksOpen(true); // apre modal invito-a-condividere
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally { setSubmitting(false); }
  };

  const handleDeleteReview = async (rid) => {
    if (!window.confirm("Eliminare questa esperienza?")) return;
    try {
      await api.delete(`/reviews/${rid}`);
      toast.success("Esperienza eliminata");
      await loadAll();
    } catch {
      toast.error("Errore");
    }
  };

  if (!school) {
    return <div className="max-w-7xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento…</div>;
  }

  // SEO: dynamic title, meta description, canonical, JSON-LD Schema.org
  if (typeof document !== "undefined") {
    document.title = `${school.name} — ${school.city} (${school.province}) | Per manina`;
    const desc = `${school.type === "pubblica" ? "Scuola pubblica" : "Scuola paritaria/privata"} a ${school.city}. ${school.address}${school.review_count ? ` — ★ ${school.avg_rating} su ${school.review_count} esperienze` : ""}. Esperienze dei genitori su Per manina.`;
    let m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", desc);
    // Robots meta: sempre index,follow — ora che ogni pagina scuola ha il paragrafo
    // SEO auto-generato (SchoolSeoParagraph) non c'è più il rischio di thin content.
    let rob = document.querySelector('meta[name="robots"]');
    if (!rob) { rob = document.createElement("meta"); rob.setAttribute("name","robots"); document.head.appendChild(rob); }
    rob.setAttribute("content", "index, follow");
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel","canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `${window.location.origin}/scuola/${school.id}`);
    // JSON-LD
    let ld = document.getElementById("school-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "school-jsonld"; document.head.appendChild(ld); }
    const json = {
      "@context": "https://schema.org",
      "@type": "School",
      "name": school.name,
      "url": `${window.location.origin}/scuola/${school.id}`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": school.address,
        "addressLocality": school.city,
        "postalCode": school.postal_code || undefined,
        "addressRegion": school.province,
        "addressCountry": "IT",
      },
      "telephone": school.phone || undefined,
      "email": school.email || undefined,
    };
    if (school.review_count > 0) {
      json.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": school.avg_rating,
        "reviewCount": school.review_count,
        "bestRating": 5,
        "worstRating": 1,
      };
    }
    ld.textContent = JSON.stringify(json);
  }

  const total = reviewsData.total || 0;
  const typeBadge = school.type === "pubblica" ? "text-[#1A362D]" : "text-[#C05C45]";
  const typeLabel = school.type === "pubblica" ? "Scuola Pubblica" : "Scuola Paritaria/Privata";
  const userReviewed = reviewsData.items.some((r) => user && r.user_id === user.user_id);

  return (
    <div data-testid={`school-detail-${school.id}`}>
      {/* HERO IMAGE */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={imageForSchool(school.id)} alt="Illustrazione scuola dell'infanzia" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,54,45,0.15) 0%, rgba(250,249,246,0.95) 95%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 sm:pt-28 sm:pb-14">
          <span className={`label-eyebrow ${typeBadge} bg-white/85 px-2.5 py-1 rounded`}>{typeLabel}</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A362D] tracking-tight leading-[1.05] max-w-3xl">
            {school.name}
          </h1>
          <div className="mt-4 font-ui text-sm text-[#1A362D]/85 flex items-start gap-2 max-w-2xl">
            <MapPin size={16} className="mt-0.5" />
            <span>{school.address}{school.postal_code ? ` — ${school.postal_code}` : ""} {school.city} ({school.province}), {school.region}</span>
          </div>
          <p className="mt-2 font-ui text-xs italic text-[#5C6B64]">Immagine illustrativa generata dall'intelligenza artificiale.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        {/* Two columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left: info + reviews */}
          <main className="md:col-span-8 space-y-10">
            {/* QUICK INFO */}
            <section className="card-surface p-6 md:p-8">
              <p className="label-eyebrow text-[#C05C45]">Informazioni</p>
              <h2 className="mt-1 font-display text-2xl text-[#1A362D] tracking-tight">Contatti e dettagli</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-ui text-sm">
                {school.phone && (
                  <a href={`tel:${school.phone}`} className="flex items-start gap-3 hover:text-[#C05C45]">
                    <Phone size={16} className="mt-0.5 text-[#5C6B64]" />
                    <div><p className="label-eyebrow">Telefono</p><p className="text-[#1A362D]">{school.phone}</p></div>
                  </a>
                )}
                {school.email && (
                  <a href={`mailto:${school.email}`} className="flex items-start gap-3 hover:text-[#C05C45] min-w-0">
                    <Mail size={16} className="mt-0.5 text-[#5C6B64]" />
                    <div className="min-w-0"><p className="label-eyebrow">Email</p><p className="text-[#1A362D] truncate">{school.email}</p></div>
                  </a>
                )}
                {school.pec && (
                  <a href={`mailto:${school.pec}`} className="flex items-start gap-3 hover:text-[#C05C45] min-w-0" data-testid="school-pec">
                    <ShieldCheck size={16} className="mt-0.5 text-[#5C6B64]" />
                    <div className="min-w-0"><p className="label-eyebrow">PEC</p><p className="text-[#1A362D] truncate">{school.pec}</p></div>
                  </a>
                )}
                {school.website && (
                  <a href={school.website} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-[#C05C45] min-w-0">
                    <Globe size={16} className="mt-0.5 text-[#5C6B64]" />
                    <div className="min-w-0"><p className="label-eyebrow">Sito web</p><p className="text-[#1A362D] truncate">{school.website}</p></div>
                  </a>
                )}
                {!school.phone && !school.email && !school.pec && !school.website && (
                  <p className="text-[#8A9691] sm:col-span-2">Nessun contatto disponibile da fonte ufficiale.</p>
                )}
              </div>
              {school.description && (
                <p className="mt-6 font-ui text-sm text-[#5C6B64] leading-relaxed">{school.description}</p>
              )}
              <SchoolSeoParagraph school={school} />
              <div className="mt-6 pt-4 border-t border-[#E8E6E1] flex items-center justify-between flex-wrap gap-2">
                {school.community_contributed && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-ui bg-[#DCE4DC] text-[#1A362D] px-2 py-1 rounded">
                    ✓ Aggiunta dalla community
                  </span>
                )}
                <Link to={`/scuola/${school.id}/correggi`} data-testid="report-correction-btn" className="ml-auto text-xs font-ui text-[#C05C45] hover:underline">
                  I dati sono sbagliati? Segnala una correzione →
                </Link>
              </div>
            </section>

            {/* REVIEWS */}
            <section>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="font-display text-3xl text-[#1A362D] tracking-tight">Le esperienze di genitori e professionisti</h2>
                {!showForm && (
                  user ? (
                    userReviewed ? (
                      <p className="font-ui text-xs text-[#5C6B64]">Hai già raccontato la tua esperienza. La trovi nella tua <a href="/area-personale" className="underline text-[#C05C45]">area personale</a>.</p>
                    ) : (
                      <button
                        onClick={openReviewForm}
                        data-testid="write-review-btn"
                        className="btn-primary flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
                      >
                        <Pencil size={16} /> Racconta la tua esperienza
                      </button>
                    )
                  ) : (
                    <button
                      onClick={handleLogin}
                      data-testid="login-to-review-btn"
                      className="btn-primary text-sm sm:text-base whitespace-nowrap"
                    >
                      Accedi e racconta la tua esperienza
                    </button>
                  )
                )}
              </div>

              {showForm && user && (
                <form onSubmit={submitReview} className="card-surface p-6 mb-6 space-y-6" data-testid="review-form">
                  {/* 1. Badge ruolo in cima — la scheda parte dalla "richiesta" di ruolo (deriva dal profilo) */}
                  <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="review-form-role-header"
                  >
                    <span className="font-ui text-sm text-[#1A362D] font-medium">
                      Stai raccontando come:
                    </span>
                    <span
                      data-testid={`review-form-role-badge-${reviewerRole}`}
                      className={`inline-flex items-center gap-1.5 text-[12px] font-ui font-medium px-2.5 py-1 rounded-full ${isProfessional ? "bg-[#FAF3E7] text-[#C05C45] border border-[#C05C45]/25" : "bg-[#DCE4DC] text-[#1A362D] border border-[#1A362D]/15"}`}
                    >
                      {isProfessional ? "👩‍🏫 Professionista" : "👨‍👩‍👧 Genitore"}
                    </span>
                    <Link to="/area-personale" className="font-ui text-xs text-[#C05C45] hover:underline">
                      Cambia ruolo
                    </Link>
                  </div>

                  {/* 2. Intro responsabilità — sostituisce la domanda "Quanto consiglieresti..." */}
                  <div
                    data-testid="review-form-intro"
                    className="rounded-lg bg-[#F4F1EA] border border-[#E8E6E1] px-4 py-3"
                  >
                    <p className="font-ui text-[14px] text-[#1A362D] leading-relaxed">
                      Per manina parte dal presupposto che ogni scuola dell'infanzia sia valida. Ma le informazioni importanti per chi deve ancora fare l'iscrizione saranno il tuo punto di vista e le tue note in fondo. Agisci con responsabilità e serenità, nel rispetto di chi leggerà: un genitore curioso, come te.
                    </p>
                  </div>

                  {/* 2. 5 dimensioni emoji — opzionali, max 2 vuote */}
                  <div className="pt-2 border-t border-[#E8E6E1] space-y-4">
                    <p className="font-ui text-xs text-[#8A9691]">Puoi lasciarne vuote al massimo 2 su 5. Più valutazioni dai, più aiuti gli altri genitori a scegliere con consapevolezza.</p>

                    {[
                      ["teachers_rating", isProfessional
                        ? "Quanta disponibilità ti impegni a dare ai genitori?"
                        : "Come valuti il rapporto tra le educatrici?"],
                      ["spaces_rating", "Come valuti gli spazi?"],
                      ["communication_rating", isProfessional
                        ? "Come valuti la collaborazione con le altre educatrici?"
                        : "Come valuti la comunicazione educatrici-genitori?"],
                      ["food_rating", "Come valuti la mensa?"],
                      ["activities_rating", isProfessional
                        ? "Quanto cerchi di rendere diversificate/creative le attività per la classe?"
                        : "Come valuti le attività/laboratori in cui sono coinvolti i tuoi figli?"],
                    ].map(([key, label]) => (
                      <div key={key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <label className="font-ui text-sm text-[#1A362D] flex-1">{label}</label>
                        <EmojiRating
                          value={dims[key]}
                          onChange={(v) => setDims((s) => ({ ...s, [key]: v }))}
                          testIdPrefix={`review-input-${key.replace(/_/g, "-")}`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* 3. Would choose again — opzionale */}
                  <div className="pt-4 border-t border-[#E8E6E1]">
                    <p className="font-ui text-[15px] text-[#1A362D] font-medium">Se potessi tornare indietro, sceglieresti di nuovo questa scuola? <span className="font-ui text-xs text-[#8A9691] font-normal">(opzionale)</span></p>
                    <div className="mt-3 flex flex-wrap gap-4 font-ui text-sm">
                      {[
                        ["si", "Sì"],
                        ["no", "No"],
                        ["non_so", "Non so"],
                      ].map(([val, label]) => (
                        <label key={val} className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="would_choose_again"
                            value={val}
                            checked={wouldChoose === val}
                            onChange={() => setWouldChoose(val)}
                            data-testid={`review-input-choose-again-${val}`}
                            className="accent-[#C05C45]"
                          />
                          <span className="text-[#1A362D]">{label}</span>
                        </label>
                      ))}
                      {wouldChoose && (
                        <button
                          type="button"
                          onClick={() => setWouldChoose(null)}
                          className="text-xs text-[#8A9691] hover:text-[#C05C45] underline"
                          data-testid="review-input-choose-again-clear"
                        >
                          Cancella scelta
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 4. Open text — obbligatorio */}
                  <div className="pt-4 border-t border-[#E8E6E1]">
                    <label htmlFor="review-open-text" className="font-ui text-[15px] text-[#1A362D] font-medium block">
                      Qual è la cosa che vorresti far sapere a un genitore che sta pensando di scegliere questa scuola?
                    </label>
                    <p className="mt-1 font-ui text-xs text-[#8A9691]">Anche una sola frase è sufficiente — è la parte che gli altri genitori leggeranno per prima.</p>
                    <textarea
                      id="review-open-text"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Scrivi con parole tue, senza pensarci troppo…"
                      data-testid="review-input-text"
                      rows={4}
                      maxLength={2000}
                      className="mt-3 w-full border border-[#E8E6E1] rounded-md p-3 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none"
                    />
                    <div
                      data-testid="review-char-counter"
                      className={`mt-1 font-ui text-xs text-right ${
                        comment.trim().length >= 20 ? "text-[#5C8B6E]" : "text-[#8A9691]"
                      }`}
                    >
                      {comment.trim().length < 20
                        ? `${comment.trim().length}/20 — ancora ${20 - comment.trim().length} caratteri`
                        : `${comment.trim().length} caratteri ✓`}
                    </div>
                  </div>

                  {/* Disclaimer + privacy */}
                  <div className="pt-2"><ReviewDisclaimer /></div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="review-privacy-checkbox" className="mt-1 accent-[#C05C45]" />
                    <span className="font-ui text-xs text-[#5C6B64] leading-relaxed">
                      Confermo che quanto raccontato è autentico e rispetta le linee guida. Accetto il trattamento dei dati come da <a href="/info/privacy" className="underline text-[#C05C45]">Privacy Policy</a>.
                    </span>
                  </label>

                  <div className="flex gap-2">
                    <button type="submit" disabled={submitting} data-testid="submit-review-btn" className="btn-primary text-sm disabled:opacity-50">{submitting ? "Invio…" : "Invia la tua esperienza"}</button>
                    <button type="button" onClick={() => setShowForm(false)} data-testid="cancel-review-btn" className="btn-secondary text-sm">Annulla</button>
                  </div>
                </form>
              )}

              {reviewsData.items.length === 0 ? (
                <div className="card-surface p-10 text-center">
                  <p className="font-display text-2xl text-[#1A362D]">Ancora nessuna esperienza raccontata</p>
                  <p className="mt-2 font-ui text-sm text-[#5C6B64]">Sii il primo genitore a condividere la sua esperienza.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviewsData.items.map((r) => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                      onReload={reloadReviews}
                      onDelete={user && r.user_id === user.user_id ? handleDeleteReview : undefined}
                    />
                  ))}
                </div>
              )}
            </section>
          </main>

          {/* Right: sticky summary */}
          <aside className="md:col-span-4">
            <div className="card-surface p-6 sticky top-24">
              <p className="label-eyebrow">Esperienze raccolte</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span
                  data-testid="school-reviews-count"
                  className="font-display text-5xl text-[#1A362D]"
                >
                  {total}
                </span>
                <span className="font-ui text-sm text-[#8A9691]">
                  {total === 1 ? "recensione" : "recensioni"}
                </span>
              </div>
              <p className="mt-3 font-ui text-xs text-[#5C6B64] leading-relaxed">
                Ogni scuola dell'infanzia è valida. Qui raccogliamo il punto di vista di chi la frequenta — per aiutare chi deve ancora scegliere.
              </p>

              <div className="mt-6 divider pt-6 space-y-2">
                <ShareMenu
                  type="scuola"
                  slug={school.id}
                  title={`${school.name} — ${school.city}`}
                />
              </div>
            </div>
            {user?.is_admin && <PremiumSidebarAd />}
            {/* AdSense slot colonna destra, desktop. Attivo solo quando AD_SLOTS.SCHOOL_DETAIL_RIGHT è compilato */}
            <AdSlot
              slot={AD_SLOTS.SCHOOL_DETAIL_RIGHT}
              format="auto"
              className="hidden lg:block"
              testId="adslot-school-detail-right"
            />
          </aside>
        </div>
      </div>

      {/* Thank-you modal con invito a condividere ad altri genitori */}
      {thanksOpen && (
        <ShareThanksModal school={school} onClose={() => setThanksOpen(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShareThanksModal — appare subito dopo l'invio dell'esperienza.
// Reward n.1 (visivo primario): download immediato della guida PDF "Le 10 regole d'oro".
// Reward n.2: invito a condividere la scheda scuola con altri genitori (WhatsApp, mail, copia link).
// ---------------------------------------------------------------------------
function ShareThanksModal({ school, onClose }) {
  const shareUrl = `${window.location.origin}/scuola/${school.id}?utm_source=share&utm_medium=parent_referral&utm_campaign=post_review`;
  const shareText = `Ho appena raccontato la mia esperienza su ${school.name} su Per manina. Se anche tu conosci questa scuola, aiuta altri genitori a scegliere con consapevolezza:`;
  const wa = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
  const mail = `mailto:?subject=${encodeURIComponent("Un aiuto per scegliere la scuola d'infanzia")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;

  const GUIDE_URL = "/downloads/pronti-alla-primaria-permanina.pdf";
  const [downloaded, setDownloaded] = useState(false);

  const onGuideDownload = () => {
    setDownloaded(true);
    trackEvent("guide_downloaded", {
      guide: "pronti_alla_primaria",
      school_id: school.id,
      trigger: "post_review",
    });
  };

  const nativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Per manina — ${school.name}`, text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copiato. Ora incollalo dove vuoi condividerlo.");
      }
      trackEvent("post_review_share", { channel: navigator.share ? "native" : "clipboard", school_id: school.id });
    } catch {}
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato negli appunti");
      trackEvent("post_review_share", { channel: "copy", school_id: school.id });
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="thanks-title"
      data-testid="post-review-share-modal"
      className="fixed inset-0 z-50 bg-[#1A362D]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#FBF8F3] rounded-lg shadow-2xl max-w-lg w-full p-6 sm:p-8 relative my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="post-review-share-close"
          aria-label="Chiudi"
          className="absolute top-3 right-4 text-[#8A9691] hover:text-[#1A362D] font-ui text-xl leading-none"
        >
          ×
        </button>

        {/* ─────────── BLOCCO 1 — DOWNLOAD GUIDA (reward primario) ─────────── */}
        <p className="label-eyebrow text-[#C05C45]">GRAZIE A TE 💛</p>
        <h3
          id="thanks-title"
          className="mt-1 font-display text-2xl sm:text-3xl text-[#1A362D] tracking-tight leading-tight"
        >
          La tua guida è pronta.
        </h3>
        <p className="mt-3 font-ui text-sm text-[#5C6B64] leading-relaxed">
          Scarica subito il PDF{" "}
          <em className="not-italic font-semibold text-[#1A362D]">
            "Le 10 regole d'oro per accompagnare tuo figlio alla primaria"
          </em>
          , scritto in collaborazione con Valeria Vergani, insegnante nella scuola
          dell'infanzia dal 1991.
        </p>

        <div className="mt-5 flex items-stretch gap-3 bg-white border border-[#E8E6E1] rounded-lg p-4">
          {/* Mini copertina */}
          <div className="shrink-0 w-14 h-20 bg-[#FAF3E7] rounded-sm shadow-[0_6px_14px_-6px_rgba(0,0,0,0.3)] rotate-[-4deg] flex flex-col justify-between p-1.5 relative">
            <span className="font-display text-[9px] text-[#1A362D] leading-none">
              Pronti alla primaria?
            </span>
            <span className="font-ui text-[6px] text-[#8A9691] uppercase tracking-wider">
              Per manina
            </span>
            <span
              aria-hidden="true"
              className="absolute -right-2 top-2 rotate-[14deg] border border-[#C05C45]/70 rounded px-1 text-[6px] font-bold text-[#C05C45] uppercase tracking-wider"
              style={{ fontFamily: '"Courier New", monospace' }}
            >
              gratis
            </span>
          </div>
          {/* Info file */}
          <div className="flex-1 flex flex-col justify-center min-w-0">
            <p className="font-ui text-[13px] font-semibold text-[#1A362D] truncate">
              Le 10 regole d'oro — Per manina.pdf
            </p>
            <p className="font-ui text-[11px] text-[#8A9691] mt-0.5">
              7 pagine · PDF · gratuito
            </p>
          </div>
          {/* CTA */}
          <a
            href={GUIDE_URL}
            download="Le-10-regole-doro-per-manina.pdf"
            onClick={onGuideDownload}
            data-testid="post-review-guide-download"
            className="shrink-0 self-center btn-primary text-sm px-4 py-2.5 whitespace-nowrap"
          >
            {downloaded ? "Scaricata ✓" : "Scarica PDF"}
          </a>
        </div>

        {downloaded && (
          <p className="mt-3 font-ui text-[12px] text-[#5C6B64] italic text-center">
            Il download è partito. Controlla la cartella download del tuo dispositivo.
          </p>
        )}

        {/* Divider */}
        <div className="mt-7 pt-6 border-t border-[#E8E6E1]">
          {/* ─────────── BLOCCO 2 — INVITO A CONDIVIDERE ─────────── */}
          <p className="font-display text-lg text-[#1A362D] leading-tight">
            Ora aiuta anche altri genitori.
          </p>
          <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
            Ogni genitore che conosce questa scuola può aggiungere un tassello. Più
            esperienze si sommano, più chiara diventa la scelta per chi arriverà dopo di te.
          </p>

          <div className="mt-4 space-y-3">
            <button
              type="button"
              onClick={nativeShare}
              data-testid="post-review-share-native"
              className="w-full btn-secondary text-sm py-2.5"
            >
              Condividi la scheda della scuola
            </button>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("post_review_share", { channel: "whatsapp", school_id: school.id })}
                data-testid="post-review-share-whatsapp"
                className="btn-secondary text-sm text-center py-2.5"
              >
                WhatsApp
              </a>
              <a
                href={mail}
                onClick={() => trackEvent("post_review_share", { channel: "email", school_id: school.id })}
                data-testid="post-review-share-mail"
                className="btn-secondary text-sm text-center py-2.5"
              >
                Email
              </a>
            </div>
            <button
              type="button"
              onClick={copyLink}
              data-testid="post-review-share-copy"
              className="w-full font-ui text-xs text-[#8A9691] hover:text-[#C05C45] underline pt-1"
            >
              oppure copia il link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
